import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, HttpError } from '@/api/client';
import { BusinessPage } from '@/features/business-page/BusinessPage';
import type { BusinessPageData } from '@/types';
import NotFound from './NotFound';

export default function BusinessDetail() {
  const { state = '', city = '', slug = '' } = useParams<{
    state: string;
    city: string;
    slug: string;
  }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['business-page', state, city, slug],
    queryFn: () =>
      apiClient<BusinessPageData>(
        `/public/businesses/${state}/${city}/${slug}`,
      ),
    staleTime: 5 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof HttpError && err.status === 404) return false;
      return failureCount < 2;
    },
    enabled: Boolean(state && city && slug),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
          <p className="mt-4 text-sm text-slate-500">Loading business page...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    if (error instanceof HttpError && error.status === 404) {
      return <NotFound message="This business page could not be found." />;
    }
    return (
      <div className="container-page py-16">
        <div className="rounded-brand border border-red-200 bg-red-50 p-8 text-center text-red-700">
          {(error as Error).message || 'Failed to load business page.'}
        </div>
      </div>
    );
  }

  if (!data) return <NotFound />;

  return <BusinessPage business={data} />;
}
